import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    LinearProgress,
    makeStyles,
    TextField,
} from '@material-ui/core';
import L from 'leaflet';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';


import { dataGeoServiciosFetch } from '../../helpers/fetch';

const useStyles = makeStyles({
    userShapeMap: {
        width: '100%',
        height: '400px',
    },
});

const UserShapesModal = ({ close, initialGeojson, open }) => {
    const uid = useSelector((state) => state.auth.uid);

    const [leafletGeojson, setLeafletGeojson] = useState(null);
    const [map, setMap] = useState(null);
    const [shapeIndex, setShapeIndex] = useState(0);
    const [descriptions, setDescriptions] = useState(null);
    const [loading, setLoading] = useState(false);

    const classes = useStyles();

    /**
     * Executes when open prop changes.
     *
     * If open is true, it means that the modal was opened, so it stores the value of the prop initialGeojson in
     * "leafletGeojson" state and initializes descriptions state.
     *
     * If open is false, it means that the modal was closed, so it restores the states to their initial values.
     */
    useEffect(() => {
        if (open) {
            setLeafletGeojson(L.geoJSON(initialGeojson));
            setDescriptions(
                initialGeojson.features.map((feature) =>
                    feature.properties && feature.properties.descripcion ? feature.properties.descripcion : ''
                )
            );
        } else {
            setLeafletGeojson(null);
            setMap(null);
            setShapeIndex(0);
            setDescriptions(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    /**
     * Executes when shapeIndex changes.
     *
     * If the map is already initialized, adds the shape indicated by shapeIndex to the map.
     */
    useEffect(() => {
        if (map) {
            map.eachLayer((layer) => {
                if (layer instanceof L.TileLayer) {
                    return;
                }
                map.removeLayer(layer);
            });
            const shapeLayer = leafletGeojson.getLayers()[shapeIndex];
            if(shapeLayer){
                shapeLayer.addTo(map);
            }
            
            if (shapeLayer instanceof L.Marker) {
                map.setView(shapeLayer.getLatLng(), 10);
            } else if (shapeLayer instanceof L.Polyline || shapeLayer instanceof L.Polygon) {
                map.fitBounds(shapeLayer.getBounds());
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, shapeIndex]);

    /**
     * Creates map. Creates instance and stores it in map state.
     */
    const createMap = () => {
        const newMap = L.map('userShapeMap');
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap contributors</a>',
        }).addTo(newMap);
        setMap(newMap);
    };

    /**
     * Builds user shapes geoJSON, sets property "descripcion" of each shape and sends geoJSON to service to save it.
     */
    const guardar = async () => {
        const geojson = leafletGeojson.toGeoJSON();
        geojson.features.forEach((feature, index) => {
            if (feature.properties) {
                feature.properties.descripcion = descriptions[index];
            } else {
                feature.properties = { descripcion: descriptions[index] };
            }
        });
        setLoading(true);
        await dataGeoServiciosFetch({ solicitud: 'shapes', data: geojson, idCliente: uid }, 'POST');
        setLoading(false);
        close();
    };

    return (
        <Dialog fullWidth maxWidth="xs" onEntered={createMap} open={open}>
            <DialogContent>
                {loading && <LinearProgress />}
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <div className={classes.userShapeMap} id="userShapeMap" />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Descripción"
                            onChange={(event) =>
                                setDescriptions((currentDescriptions) => {
                                    const descriptionsCopy = [...currentDescriptions];
                                    descriptionsCopy[shapeIndex] = event.target.value;
                                    return descriptionsCopy;
                                })
                            }
                            value={descriptions ? descriptions[shapeIndex] : ''}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Grid container justify="space-between" spacing={2}>
                    <Grid item>
                        <Button color="primary" disabled={loading} onClick={close} variant="contained">
                            Cancelar
                        </Button>
                    </Grid>
                    <Grid item>
                        <Grid container spacing={2}>
                            {shapeIndex > 0 && (
                                <Grid item>
                                    <Button
                                        color="primary"
                                        disabled={loading}
                                        onClick={() => setShapeIndex((currentShapeIndex) => currentShapeIndex - 1)}
                                        variant="contained"
                                    >
                                        Anterior
                                    </Button>
                                </Grid>
                            )}
                            {leafletGeojson && shapeIndex < leafletGeojson.getLayers().length - 1 ? (
                                <Grid item>
                                    <Button
                                        color="primary"
                                        onClick={() => setShapeIndex((currentShapeIndex) => currentShapeIndex + 1)}
                                        variant="contained"
                                    >
                                        Siguiente
                                    </Button>
                                </Grid>
                            ) : (
                                <Grid item>
                                    <Button color="primary" disabled={loading} onClick={guardar} variant="contained">
                                        Guardar
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>
    );
};

UserShapesModal.propTypes = {
    close: PropTypes.func,
    initialGeojson: PropTypes.any,
    open: PropTypes.bool,
};

export default UserShapesModal;
